import {GlazingItemLabel, PurchaseOrder, PurchaseOrderItem} from "../../../../api/Logistics";
import {forwardRef, useMemo} from "react";
import {Page, pdfTableStyle} from "../../../../misc/pdf/parts/Page";
import moment from "moment";
import {formatCents, groupByArr} from "nate-react-api-helpers";
import {DataSplitInfo, PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {pickup} from "../PurchaseOrderList";
import {PDF} from "../../../../misc/pdf/parts/PDF";
import {pdfInfo} from "../../../../misc/pdf/parts/CoverPage";
import {useReadyCheck} from "../../../../misc/pdf/UseReadyCheck";
import {PDFHeader} from "../../../../misc/pdf/parts/PDFHeader";
import {HardwareDetailItem, HardwarePrepTable} from "./HardwarePrepTable";
import {table, tableish} from "../../../../misc/pdf/parts/TableStyle";
import {FrameDetails, useFrameDetails} from "./FrameDetails";
import {DoorDetails, useDoorDetails} from "./DoorDetails";
import {KeyDetails2, KeyingDetails} from "./KeyingDetails";
import {PrepDiagram} from "./MakeHwPrepDiagrams";
import {ImageTilePage2} from "./ImageTilePage";
import { DoorElevationDetails } from "./DoorElevationDetails";
import {grey} from "@mui/material/colors";
import {GlazingDetail, GlazingDetails} from "./GlazingDetails";

export const taxRate = 13;

export const purchaseOrderCSV = function (props: {
    value: PurchaseOrder;
    items: PurchaseOrderItem[]
    includePricing?: boolean;
}) {
    const {sum, rows, date, hasOpenings} = buildData(props);
    const out: string[][] = [];

    out.push(["Purchase Order"])
    out.push(["Date", date]);
    out.push(["P.O. number", props.value.poNumber])
    out.push(["Vendor", props.value.supplierName])
    out.push([])
    out.push(["Item", ...(hasOpenings ? ["Openings"] : []), "Qty", "Rate", "Amount"]);

    out.push(...rows.map(i => [
        combineContent(" - ", i.description, i.productCode),
        ...(hasOpenings ? [i.openingNames] : []),
        i.qty,
        i.unitAmount,
        i.amount
    ]))
    out.push([...(hasOpenings ? [""] : []), "", "",`Total`, formatCents(sum)])

    return out;
}

export const dateFormat = "MM/DD/YYYY"

function buildData(props: {
    value: PurchaseOrder;
    items: PurchaseOrderItem[];
    includePricing?: boolean;
    priceRequest?: boolean;
}) {
    let sum = 0;

    let rows = groupByArr(props.items, i => i.description + i.code)
        .map(c => {
            const qty = c.values.reduce((acc, v) => v.qty + acc, 0);
            const amount = props.includePricing ? Math.round(c.values[0].unitPriceCents * qty) : 0;
            sum += amount;

            const isDoorOrFrame = c.values[0].kind === "door" || c.values[0].kind === "frame";

            return {
                isDoorOrFrame: isDoorOrFrame,
                description: c.values[0].description,
                labels: glazingLabelLine(c.values.flatMap(v => v.glazingLabels || [])),
                unitAmount: formatCents(props.includePricing ? c.values[0].unitPriceCents : 0),
                amount: formatCents(amount),
                qty: qty.toString(),
                productCode: c.values[0].code,
                openingNames: isDoorOrFrame ? Array.from(new Set(
                    c.values.flatMap(v => v.openings || []).map(o => o.name).filter(n => !!n)
                )).join(", ") : "",
            }
        });

    let tax = Math.round(sum * taxRate/100);

    rows.push({
        description: "HST (ON) on purchases (Input Tax Credit)",
        qty: "",
        productCode: "",
        amount: formatCents(tax),
        unitAmount: taxRate + "%",
        isDoorOrFrame: false,
        openingNames: "",
        labels: "",
    })

    sum += tax;

    const subDate = props.priceRequest ? moment() : moment(props.value.submittedAt);
    const date = subDate.isValid() ? subDate.format(dateFormat) : "Invalid Submitted At Date";

    return {
        sum,
        tax,
        rows,
        date,
        hasOpenings: rows.some(r => !!r.openingNames),
    }
}

// "{elevation}-{variant}-{mark} - {qty}" per mark, so the glazier can tell which pane on which
// elevation each line covers. Variants of one elevation carry the sub-label (A, B, C…). A pane
// listed twice in one row (double glazing) counts twice.
function glazingLabelLine(labels: GlazingItemLabel[]): string {
    const qtyByMark = new Map<string, number>();

    labels.forEach(l => {
        const perPane = l.panels.length > 0 ? l.qty / l.panels.length : 0;
        const variant = l.label ? l.elevation + "-" + l.label : l.elevation;

        l.panels.forEach(p => {
            const key = variant + "-" + p.mark;
            qtyByMark.set(key, (qtyByMark.get(key) || 0) + perPane);
        });
    });

    return Array.from(qtyByMark.entries())
        .map(([key, qty]) => key + " @ " + Math.round(qty))
        .join(", ");
}

export type DoorElevationDetail = {
    elevation: string,
    elevationFile: number | null,
    elevationFileContent: string | null
    profile: number | null
    profileContent: string | null
}

export const PurchaseOrderPDF = forwardRef(function PurchaseOrderPDF(props: {
    value: PurchaseOrder;
    priceRequest?: boolean;
    items: PurchaseOrderItem[]
    prepDiagrams: PrepDiagram[];
    hardwareDetail: HardwareDetailItem[];
    keyDetails: KeyDetails2;
    doorElevations: DoorElevationDetail[]
    glazing?: GlazingDetail;
    includePricing?: boolean;
    onReady(): void;
}, ref: any) {
    const {sum, rows, date, hasOpenings} = useMemo(() => buildData(props), [props]);
    const ready = useReadyCheck(props.onReady);
    const section = ready.section();

    const frames = useFrameDetails(props.items, props.hardwareDetail)
    const doors = useDoorDetails(props.items, props.hardwareDetail)

    const hasKeyedItem = useMemo(() => {
        return props.items.some(h => !!h.hwKeyed);
    }, [props.items]);

    return (
        <div ref={ref}>
            <PDF>
                <PageRenderProvider key="page-render" debugKey="po-items" data={rows} onReady={section}>
                    {(info: DataSplitInfo<{
                        description: string;
                        unitAmount: string;
                        amount: string;
                        qty: string;
                        productCode: string;
                        openingNames: string;
                        isDoorOrFrame: boolean;
                        labels: string;
                    }>) =>
                        <Page orientation="portrait" key={info.pageIndex.toString()}>
                            <div style={{flex: 1}}>
                                <PDFHeader noAddress title={props.priceRequest ? "Price Request" : "Purchase Order"}>
                                    <table className={table}>
                                        <tr>
                                            <td>Date</td>
                                            {!props.priceRequest && <td>P.O. No.</td>}
                                        </tr>
                                        <tr>
                                            <td>{date}</td>
                                            {!props.priceRequest && <td>{props.value.poNumber}</td>}
                                        </tr>
                                    </table>
                                </PDFHeader>

                                <div style={{display: "flex", justifyContent: "space-between", fontSize: "0.8rem"}}>
                                    <div>
                                        <div className={tableish}>
                                            <div>Vendor:</div>
                                            <div>
                                                <div>{props.value.supplierName}</div>
                                                <div>{props.value.supplierAddress}</div>
                                                <div>{props.value.supplierCity}, {props.value.supplierProvince}</div>
                                                <div>{props.value.supplierPostal}</div>
                                            </div>
                                        </div>
                                        <div className={tableish} style={{marginTop: 20}}>
                                            <div>Project:</div>
                                            <div>
                                                <div>{props.value.linkedProjects?.map(p => p.name).join(", ")}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className={tableish}>
                                            <div>Ship To:</div>
                                            <div>
                                                {props.value.deliveryMethod === pickup ?
                                                    <>We will pick up</> : <>
                                                        <div>{pdfInfo.address1}</div>
                                                        <div>{pdfInfo.address2}</div>
                                                        <div>{pdfInfo.postal}</div>
                                                    </>}
                                            </div>
                                        </div>
                                        <div style={{paddingTop: 20}}>
                                            <table className={table}>
                                                <tr>
                                                    <td>Terms</td>
                                                </tr>
                                                <tr>
                                                    <td>Net 30</td>
                                                </tr>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div style={{height: 16}} />

                                {info.pageIndex === 0 && <div style={{fontSize: "0.8rem", marginBottom: 8}}>
                                    <div style={{fontWeight: "bold", fontSize: "1.1rem"}}>
                                        Target pickup date: {props.value.targetPickupDate ? moment(props.value.targetPickupDate).format("MMM D, YYYY") : ""}
                                    </div>
                                    {!!props.value.vendorInstructions && <div style={{paddingTop: 12, paddingBottom: 12}}>
                                        <div style={{fontWeight: "bold"}}>Vendor Instructions</div>
                                        <div
                                            style={{fontWeight: "normal", marginTop: 4}}
                                            dangerouslySetInnerHTML={{__html: props.value.vendorInstructions}}
                                        />
                                    </div>}
                                </div>}

                                <table className={pdfTableStyle}>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            {hasOpenings && <th>Openings</th>}
                                            <th>Qty</th>
                                            <th>Rate</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {info.rows.map(i => <tr>
                                        <td style={{
                                            whiteSpace: "pre-wrap",
                                        }}>
                                            {i.description.includes(i.productCode) ? i.description : combineContent(" - ", i.description, i.productCode)}
                                            {!!i.labels && <div style={{fontSize: "0.75em", color: grey["700"]}}>{i.labels}</div>}
                                        </td>
                                        {hasOpenings && <td style={{
                                            whiteSpace: "pre-wrap",
                                        }}>{i.openingNames}</td>}
                                        <td style={{textAlign: "right"}}>{i.qty}</td>
                                        <td style={{textAlign: "right"}}>{i.unitAmount}</td>
                                        <td style={{textAlign: "right"}}>{i.amount}</td>
                                    </tr>)}
                                    {info.isLastPage && <tr key="footer" className="table-footer">
                                        <td className="pdf-header" colSpan={hasOpenings ? 4 : 3} style={{textAlign: "right"}}>
                                            Total
                                        </td>
                                        <td style={{textAlign: "right"}}>
                                            {formatCents(sum)}
                                        </td>
                                    </tr>}
                                    {info.isLastPage && !!props.value.weldShopNotes && <tr key="footer" className="table-footer">
                                        <td className="pdf-header" colSpan={hasOpenings ? 5 : 4}>
                                            <div>Weld Shop Notes</div>
                                            <div style={{
                                                fontWeight: "normal",
                                                fontSize: "0.8rem",
                                            }}>
                                                {props.value.weldShopNotes}
                                            </div>
                                        </td>
                                    </tr>}
                                    </tbody>

                                </table>
                            </div>
                        </Page>}
                </PageRenderProvider>
                <FrameDetails value={frames} onReady={ready.section()} />
                <DoorDetails value={doors} onReady={ready.section()} />
                {(props.prepDiagrams.length > 0) &&
                    <ImageTilePage2 getTitle={items => {
                        return "Prep Elevation " + items[0].name
                    }}
                        images={props.prepDiagrams} rows={1} cols={1}
                    />}
                {(doors.length > 0 || frames.length > 0) && <HardwarePrepTable onReady={ready.section()}
                                                                      hasDoors={doors.length > 0}
                                                                      hasFrames={frames.length > 0}
                                                                      hardwareDetail={props.hardwareDetail}
                                                            />}
                {hasKeyedItem && props.keyDetails.rows.length > 0 && <KeyingDetails value={props.keyDetails} onReady={ready.section()} />}
                {props.doorElevations.length > 0 && <DoorElevationDetails value={props.doorElevations} />}
                {props.glazing && <GlazingDetails value={props.glazing} onReady={ready.section()} />}
            </PDF>
        </div>
    )
})

export function combineContent(separator: string, ...input: any[]) {
    return input.filter(i => !!i).join(separator)
}